import type { ChangeEvent } from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import type { LeadStatusFilter } from 'app/features/leads/services/gym-finder/types';
import TextField from 'app/ui/components/atoms/text-field';
import Typography from 'app/ui/components/atoms/typography';

const LEADS_STATUS_FILTERS: readonly LeadStatusFilter[] = ['all', 'new', 'contacted', 'attended'];

type LeadsToolbarProps = {
  search: string;
  status: LeadStatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: LeadStatusFilter) => void;
};

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(6)};

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      `,
    )}
  `}
`;

const SearchWrapper = styled.div`
  width: 100%;

  > div,
  input {
    width: 100%;
  }

  input {
    border-color: ${({ theme }) => theme.semantic.neutrals_400};
    background: transparent;
    color: ${({ theme }) => theme.semantic.text_small_elements};

    &::placeholder {
      color: ${({ theme }) => theme.semantic.neutrals_600};
    }

    &:focus,
    &:active {
      border-color: ${({ theme }) => theme.semantic.accent_500};
    }
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        max-width: 28rem;
      `,
    )}
  `}
`;

const FilterList = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
`;

const FilterLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const FilterChip = styled.button<{ $isActive: boolean }>`
  cursor: pointer;
  border-radius: ${({ theme: { spacing } }) => spacing(5)};
  border: 0.0625rem solid
    ${({ theme, $isActive }) => ($isActive ? theme.semantic.accent_500 : theme.semantic.neutrals_400)};
  background: ${({ theme, $isActive }) => ($isActive ? theme.semantic.accent_500 : 'transparent')};
  padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(4)}`};
  transition: background-color 0.2s ease-in-out;

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }
`;

export const LeadsToolbar = ({ search, status, onSearchChange, onStatusChange }: LeadsToolbarProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();

  const searchPlaceholder = intl.formatMessage({ id: 'leads.search.placeholder' });
  const searchAriaLabel = intl.formatMessage({ id: 'leads.search.ariaLabel' });
  const filtersAriaLabel = intl.formatMessage({ id: 'leads.filters.ariaLabel' });

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  return (
    <ToolbarContainer>
      <SearchWrapper data-testid="leads-search">
        <TextField
          placeholder={searchPlaceholder}
          aria-label={searchAriaLabel}
          value={search}
          onChange={handleSearchChange}
        />
      </SearchWrapper>
      <FilterList>
        <FilterLegend>{filtersAriaLabel}</FilterLegend>
        {LEADS_STATUS_FILTERS.map((filter) => {
          const isActive = filter === status;
          return (
            <FilterChip
              key={filter}
              type="button"
              $isActive={isActive}
              aria-pressed={isActive}
              data-testid={`leads-filter-${filter}`}
              onClick={() => onStatusChange(filter)}
            >
              <Typography variant="body3" color={isActive ? semantic.content_primary_button : undefined}>
                {intl.formatMessage({ id: `leads.filters.${filter}` })}
              </Typography>
            </FilterChip>
          );
        })}
      </FilterList>
    </ToolbarContainer>
  );
};

export default LeadsToolbar;
